import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CustomerForm } from "@/components/customers/forms/CustomerForm";
import { CustomerDetails } from "@/components/customers/CustomerDetails";
import { CustomerListView } from "./views/CustomerListView";
import { CustomerDetailsView } from "./views/CustomerDetailsView";
import { useCustomerData } from "./hooks/useCustomerData";
import { useCompanyMembers } from "@/components/customers/forms/useCompanyMembers";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { Customer } from "./types";

export const CustomersPage = () => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notify = useNotify();
  const { companyMembers } = useCompanyMembers();

  const {
    customers,
    filteredSortedCustomers,
    fetchCustomers,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir
  } = useCustomerData();

  useEffect(() => {
    const loadCustomers = async () => {
      setLoading(true);
      await fetchCustomers();
      setLoading(false);
    };
    loadCustomers();
  }, []);

  // Handle URL parameters
  useEffect(() => {
    const customerId = searchParams.get('customer');
    if (customerId && filteredSortedCustomers.length > 0) {
      const customer = filteredSortedCustomers.find(c => c.id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setViewMode('detail');
      }
    }
  }, [searchParams, filteredSortedCustomers]);

  // Legacy support: redirect ?action=create to the dedicated create page
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      navigate('/customers/new', { replace: true });
    }
  }, [searchParams]);

  const handleSave = async () => {
    setOpen(false);
    setEditing(null);
    await fetchCustomers();
  };

  const handleCancel = () => {
    setOpen(false);
    setEditing(null);
  };

  const viewCustomerDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setViewMode('detail');
    // Update URL to reflect the selected customer
    navigate(`/customers?customer=${customer.id}`);
  };

  const handleDeleteCustomer = async (customerId: string) => {
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      if (error) {
        console.error('Error deleting customer:', error);
        notify.error(error);
        return;
      }

      notify.success('Customer deleted successfully');

      // Refresh the customer list
      await fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      notify.error(error);
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedCustomer(null);
    // Clear customer parameter from URL
    navigate('/customers');
  };

  return (
    <>
      {viewMode === 'detail' && selectedCustomer ? (
        <CustomerDetailsView
          selectedCustomer={selectedCustomer}
          setViewMode={handleBackToList}
          setEditing={setEditing}
          setOpen={setOpen}
          handleDeleteCustomer={handleDeleteCustomer}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          navigateToCustomer={(customerId: string) => {
            const customer = filteredSortedCustomers.find(c => c.id === customerId);
            if (customer) viewCustomerDetails(customer);
          }}
        />
      ) : (
        <CustomerListView
          loading={loading}
          customers={customers}
          search={search}
          setSearch={setSearch}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDir={sortDir}
          setSortDir={setSortDir}
          setOpen={setOpen}
          setEditing={setEditing}
          viewCustomerDetails={viewCustomerDetails}
          handleDeleteCustomer={handleDeleteCustomer}
          navigate={navigate}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-montserrat">
              {editing ? "Edit Customer" : "Add New Customer"}
            </DialogTitle>
          </DialogHeader>
          <CustomerForm
            customer={editing}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
